/*
 * « ResizeObserver loop completed with undelivered notifications »
 *
 * CE QUE C'EST : un signal émis par le NAVIGATEUR, pas une exception de
 * notre code. Quand un callback `ResizeObserver` modifie la mise en page,
 * le navigateur reporte les notifications restantes à la frame suivante
 * et signale l'événement. L'interface se stabilise, aucun état n'est
 * perdu, aucune promesse n'est rejetée.
 *
 * POURQUOI ON LE VOIT QUAND MÊME : l'overlay de `webpack-dev-server`
 * intercepte `window.onerror` et transforme TOUT signal en écran rouge
 * plein cadre. En production cet overlay n'existe pas — le message ne
 * s'affiche nulle part.
 *
 * Ant Design l'émet couramment : `rc-table` mesure la largeur pour
 * décider des ombres de défilement, `rc-tabs` mesure la barre d'onglets
 * pour calculer le débordement. Ces mesures se déclenchent l'une l'autre
 * au premier rendu.
 *
 * DEUX MÉCANISMES, ET LE PREMIER EST LE VRAI CORRECTIF.
 *
 * 1. ON SUPPRIME LA CAUSE (développement seulement) : les callbacks
 *    `ResizeObserver` sont différés d'une frame. Le signal naît quand
 *    un callback modifie la mise en page PENDANT le cycle de livraison
 *    des notifications — le navigateur constate alors qu'il en reste à
 *    livrer. Exécuté hors de ce cycle, le callback ne peut plus le
 *    provoquer. Aucune entrée n'est perdue (on ne déduplique pas les
 *    frames), seul l'instant d'exécution change.
 *
 * 2. On garde la neutralisation du message comme seconde ligne, pour
 *    les signaux émis par du code qui n'utilise pas notre `ResizeObserver`
 *    (une extension de navigateur, par exemple).
 *
 * POURQUOI LE SEUL MÉCANISME 2 NE SUFFISAIT PAS — et c'était un défaut
 * latent. Il repose sur `stopImmediatePropagation`, donc sur l'ordre des
 * écouteurs. Or pour un événement `error`, `window` est la CIBLE et non
 * un ancêtre : à la phase cible, les écouteurs se déclenchent dans leur
 * ORDRE D'ENREGISTREMENT, et `capture: true` n'y donne aucune priorité.
 * `react-error-overlay` s'enregistrant au démarrage du bundle, un écran
 * chargé en `lazy` arrivait toujours après lui. D'où l'appel désormais
 * fait AUSSI depuis `src/index.js`, avant le premier rendu.
 *
 * CE QU'IL NE FAIT PAS : masquer un défaut de mise en page. Une boucle
 * réelle et continue (celle du `scroll.y` corrigée en I1-B) reste
 * visible — au profileur, à la consommation CPU, et au comportement de
 * l'écran. Ce garde n'excuse pas de laisser une boucle vivante ; il
 * empêche un signal bénin de bloquer un écran de recette.
 */
const MESSAGES_BENINS = [
  'ResizeObserver loop completed with undelivered notifications.',
  'ResizeObserver loop limit exceeded',
];

const estBenin = (texte) =>
  typeof texte === 'string' &&
  MESSAGES_BENINS.some((message) => texte.includes(message));

let pose = false;

/*
 * Diffère les callbacks `ResizeObserver` d'une frame — voir l'en-tête.
 *
 * Scopé au DÉVELOPPEMENT : c'est là, et seulement là, qu'un signal bénin
 * bloque un écran (l'overlay n'existe pas en production). Changer le
 * calendrier des mesures de toute l'application pour confortier la
 * recette serait un rayon d'action disproportionné.
 *
 * `prototype` conservé pour que `instanceof ResizeObserver` reste vrai,
 * et aucune frame n'est annulée : dédupliquer perdrait des entrées dont
 * `rc-table` a besoin pour calculer ses ombres de défilement.
 */
const differerResizeObserver = () => {
  if (process.env.NODE_ENV === 'production') {
    return;
  }
  const Natif = window.ResizeObserver;
  if (typeof Natif !== 'function' || Natif.esgcvakDiffere) {
    return;
  }
  const Differe = function ResizeObserverDiffere(callback) {
    return new Natif((entrees, observateur) => {
      window.requestAnimationFrame(() => callback(entrees, observateur));
    });
  };
  Differe.prototype = Natif.prototype;
  Differe.esgcvakDiffere = true;
  window.ResizeObserver = Differe;
};

const garderOverlayDev = () => {
  if (pose || typeof window === 'undefined') {
    return;
  }
  pose = true;
  differerResizeObserver();

  // `capture: true` : l'overlay écoute en phase de bulle, on passe avant.
  window.addEventListener(
    'error',
    (evenement) => {
      if (estBenin(evenement.message)) {
        evenement.stopImmediatePropagation();
        evenement.preventDefault();
      }
    },
    true
  );

  window.addEventListener(
    'unhandledrejection',
    (evenement) => {
      const raison = evenement.reason;
      if (estBenin(raison && raison.message ? raison.message : raison)) {
        evenement.stopImmediatePropagation();
        evenement.preventDefault();
      }
    },
    true
  );
};

export { estBenin, garderOverlayDev };
